/**
 * The tolerant unwrap of #31 (T.Z103) — deliberately framework-free (no fetch,
 * no Vite globals) so the plain-Node `unit` project can test it, the same split
 * `settings-model.ts` (parseSystemStatus) already uses.
 *
 * The contract types #31's fields the way it types #23's: presence is promised
 * in the schema but a payload is still untrusted wire data, and a changelog
 * renderer must never crash the Settings page over a shape it did not expect —
 * additive enum members and fields will arrive (the freeze policy says so).
 */

import type { ReleaseNoteBlockDto } from '../../api/release-notes';

/** One rendered release block — #31's `ReleaseNoteBlockDto` with nothing optional left. */
export type ReleaseNoteBlock = ReleaseNoteBlockDto;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** `string | null | undefined` → `string | null` — the contract's only tri-state field. */
function asCaption(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function asBlock(value: unknown): ReleaseNoteBlock | null {
  if (!isRecord(value)) return null;
  if (typeof value['tag'] !== 'string' || value['tag'].trim() === '') return null;
  if (typeof value['date'] !== 'string' || value['date'].trim() === '') return null;
  if (!Array.isArray(value['items'])) return null;
  const items = value['items'].filter((item): item is string => typeof item === 'string' && item.trim() !== '');
  return { tag: value['tag'], date: value['date'], caption: asCaption(value['caption']), items };
}

/**
 * Unwraps the #31 envelope `{data:{blocks:[…]}}` into renderable blocks, in the
 * order the api sent (file order, newest first). Anything that is not a well
 * formed block is dropped, not thrown: a malformed line in a changelog must not
 * blank the page. An empty/absent list reads as «нет данных» upstream.
 */
export function parseReleaseNotes(payload: unknown): ReleaseNoteBlock[] {
  if (!isRecord(payload) || !isRecord(payload['data'])) return [];
  const blocks = payload['data']['blocks'];
  if (!Array.isArray(blocks)) return [];
  return blocks.map(asBlock).filter((block): block is ReleaseNoteBlock => block !== null);
}

/**
 * `2026-09-16` → `16.09.2026` — the file keeps ISO dates, the page reads like the
 * rest of the RU interface. Anything the api may additively send that is not the
 * promised `YYYY-MM-DD` is shown verbatim rather than mangled.
 */
export function formatReleaseDate(date: string): string {
  const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!parts) return date;
  const [, year, month, day] = parts;
  if (!year || !month || !day) return date;
  return `${day}.${month}.${year}`;
}
